// Who AHFC is, on a claim.
//
// Every value here was read off a GAMMIS remittance advice, not inferred. The
// generator this replaces carried a hardcoded fallback for each one and every
// fallback was wrong: the DBA instead of the legal entity, an Atlanta street
// address with no connection to AHFC, and an EIN missing a digit. None of it
// was ever caught because no file was ever sent anywhere.
//
// So there are no defaults in this module. An incomplete identity is an error
// that stops the build, because the alternative is a claim filed in the wrong
// name — which is worse than a claim not filed.
//
// Source of truth is the `company_info` table, edited at Settings → General
// Info. This module is pure: it takes the rows and returns an identity, so it
// can be tested without a database.

/** company_info.label → identity field. */
const LABELS = {
  'EDI Billing Name':          'legalName',
  'NPI Number':                'npi',
  'Tax ID / EIN':              'ein',
  'Medicaid Provider Number':  'medicaidProviderId',
  'Medicaid Provider Taxonomy':'taxonomy',
  'Main Office Phone':         'phone',
  'Billing Address Line 1':    'address1',
  'Billing Address Line 2':    'address2',
  'Billing City':              'city',
  'Billing State':             'state',
  'Billing ZIP':               'zip',
  'EDI Trading Partner ID':    'tradingPartnerId',
  'EDI Usage Indicator':       'usageIndicator',
};

const digitsOnly = (v) => String(v ?? '').replace(/\D/g, '');
const upper = (v) => String(v ?? '').trim().toUpperCase();

/**
 * Build the identity from company_info rows.
 *
 * Normalising happens here so no call site has to remember: the EIN loses its
 * hyphen (REF*EI is digits only), the ZIP loses its hyphen (N403 is 5 or 9
 * digits), and names and addresses go uppercase, which GAMMIS recommends.
 */
export const identityFromCompanyInfo = (rows = []) => {
  const raw = {};
  for (const r of rows || []) {
    const key = LABELS[r?.label];
    if (key) raw[key] = r.value;
  }
  return {
    legalName:          upper(raw.legalName),
    npi:                digitsOnly(raw.npi),
    ein:                digitsOnly(raw.ein),
    medicaidProviderId: upper(raw.medicaidProviderId),
    taxonomy:           upper(raw.taxonomy),
    phone:              digitsOnly(raw.phone),
    address1:           upper(raw.address1),
    address2:           upper(raw.address2),
    city:               upper(raw.city),
    state:              upper(raw.state).slice(0, 2),
    zip:                digitsOnly(raw.zip),
    tradingPartnerId:   upper(raw.tradingPartnerId),
    // ISA15. 'T' until Gainwell EDI Operations approves all three transactions
    // in the Edifecs portal. Defaults to 'T' when unset, because a file
    // wrongly flagged production is a real claim submission and a file wrongly
    // flagged test is merely ignored — the safe failure is the quiet one.
    usageIndicator:     upper(raw.usageIndicator) === 'P' ? 'P' : 'T',
  };
};

/** Fields without which no transaction can be built at all. */
const REQUIRED = [
  ['legalName',          'EDI Billing Name'],
  ['npi',                'NPI Number'],
  ['ein',                'Tax ID / EIN'],
  ['medicaidProviderId', 'Medicaid Provider Number'],
  ['taxonomy',           'Medicaid Provider Taxonomy'],
  ['address1',           'Billing Address Line 1'],
  ['city',               'Billing City'],
  ['state',              'Billing State'],
  ['zip',                'Billing ZIP'],
];

// A PO Box anywhere in the billing provider address fails GAMMIS compliance
// outright (837P CG §7.9), so it is caught here rather than in a rejection
// notice a day later.
const PO_BOX = /\bP\.?\s*O\.?\s*BOX\b|\bPOST\s+OFFICE\s+BOX\b/i;

/**
 * Everything wrong with this identity, as human sentences. Empty means usable.
 *
 * `forTransmission` additionally requires the trading partner ID, which is not
 * issued until enrolment completes — so the identity is complete and testable
 * before AHFC has one, and only the act of building a file demands it.
 */
export const identityProblems = (identity, { forTransmission = false } = {}) => {
  const id = identity || {};
  const problems = [];

  for (const [field, label] of REQUIRED) {
    if (!id[field]) problems.push(`${label} is not set in Settings → General Info.`);
  }

  if (id.npi && id.npi.length !== 10) {
    problems.push(`NPI must be 10 digits; "${id.npi}" has ${id.npi.length}.`);
  }
  if (id.ein && id.ein.length !== 9) {
    problems.push(`Tax ID / EIN must be 9 digits; "${id.ein}" has ${id.ein.length}.`);
  }
  if (id.zip && id.zip.length !== 5 && id.zip.length !== 9) {
    problems.push(`Billing ZIP must be 5 or 9 digits; "${id.zip}" has ${id.zip.length}.`);
  }
  // Health care provider taxonomy codes are always exactly 10 characters.
  // company_info held a 9-character value ('251J0000X' for what NPPES lists as
  // '251J00000X'), which would have gone into PRV02 on every claim.
  if (id.taxonomy && id.taxonomy.length !== 10) {
    problems.push(`Medicaid Provider Taxonomy must be 10 characters; "${id.taxonomy}" has ${id.taxonomy.length}.`);
  }
  if (id.state && id.state.length !== 2) {
    problems.push(`Billing State must be the two-letter code.`);
  }
  if (PO_BOX.test(`${id.address1 || ''} ${id.address2 || ''}`)) {
    problems.push('Billing address is a PO Box. GAMMIS fails any claim whose billing provider address contains one — use the street address.');
  }
  if (forTransmission && !id.tradingPartnerId) {
    problems.push('EDI Trading Partner ID is not set. It is issued by Gainwell when EDI enrolment completes.');
  }
  // Loop 1000A PER carries the submitter's contact number and PER03/PER04 are
  // Must Use. Without a phone the pair cannot be emitted at all, and a file
  // missing it is rejected at SNIP 1 — not for the one segment, for the whole
  // interchange. Cheaper to refuse here than to find out from a 999.
  if (forTransmission && !id.phone) {
    problems.push('Main Office Phone is not set. It is the submitter contact number in loop 1000A, and GAMMIS rejects a file without it.');
  }

  return problems;
};

/**
 * The identity, or an exception naming every problem.
 *
 * Callers that render a warning should use `identityProblems` instead; this is
 * for the generators, where there is nothing sensible to emit without it.
 */
export const requireIdentity = (identity, opts) => {
  const problems = identityProblems(identity, opts);
  if (problems.length) {
    throw new Error(`Billing identity is incomplete:\n- ${problems.join('\n- ')}`);
  }
  return identity;
};
